import type {SequenceDay} from '../../machines/bookingMachine/bookingMachine.types';
import type {ITimeSlot} from '../../types/ITimeSlot';


export const SLOT_OVERLAP_ERROR_KEY = 'slot_overlap';

export type RequestedSlotValidationInput = {
  date: string;
  start_time: string;
  end_time: string;
};

export type SlotRequestLimits = {
  days: number;
  hours: number;
};

/**
 * Two half-open time ranges [startA, endA) and [startB, endB) overlap when
 * each range starts before the other ends. Times are compared as `HH:mm`
 * strings (lexicographic order matches chronological order in this format).
 */
export const slotsOverlap = (
  startA: string,
  endA: string,
  startB: string,
  endB: string,
): boolean => {
  if (!startA || !endA || !startB || !endB) {
    return false;
  }
  return startA < endB && startB < endA;
};

/**
 * Flattens the per-sequence-day `free_time_slots` into a single map keyed by
 * date so callers can look up availability for any date without knowing which
 * sequence day fetched it.
 */
export const getAvailableTimeSlotsByDate = (
  sequenceDays: SequenceDay[] | undefined,
): Record<string, ITimeSlot[]> => {
  if (!sequenceDays?.length) {
    return {};
  }

  return sequenceDays.reduce<Record<string, ITimeSlot[]>>((acc, day) => {
    const slotsByDate = day.timeSlots?.free_time_slots ?? {};
    for (const date of Object.keys(slotsByDate)) {
      const slotsForDate = slotsByDate[date] ?? [];
      if (!slotsForDate.length) {
        continue;
      }
      acc[date] = acc[date]
        ? [...acc[date], ...slotsForDate]
        : [...slotsForDate];
    }
    return acc;
  }, {});
};

export const slotDurationHours = (
  date: string,
  startTime: string,
  endTime: string,
): number | null => {
  if (!date || !startTime || !endTime) {
    return null;
  }

  const start = new Date(`${date}T${startTime}:00`);
  const end = new Date(`${date}T${endTime}:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const durationMs = end.getTime() - start.getTime();
  if (durationMs <= 0) {
    return null;
  }

  return durationMs / (1000 * 60 * 60);
};

export const parseSlotRequestLimitsFromRuleValue = (
  value: string | undefined,
): SlotRequestLimits | null => {
  if (!value) {
    return null;
  }

  const daysMatch = value.match(/['"]days['"]\s*:\s*(\d+)/);
  const hoursMatch = value.match(/['"]hours['"]\s*:\s*(\d+)/);
  const days = Number(daysMatch?.[1]);
  const hours = Number(hoursMatch?.[1]);

  if (Number.isNaN(days) || Number.isNaN(hours) || days <= 0 || hours <= 0) {
    return null;
  }

  return {days, hours};
};

export const getRequestedSlotsStats = (
  requestedSlots: RequestedSlotValidationInput[],
): {totalHours: number; distinctDays: number} => {
  let totalHours = 0;
  const distinctDays = new Set<string>();

  for (let i = 0; i < requestedSlots.length; i++) {
    const row = requestedSlots[i];
    const hours = slotDurationHours(row.date, row.start_time, row.end_time);
    if (hours !== null) {
      totalHours += hours;
      distinctDays.add(row.date);
    }
  }

  return {totalHours, distinctDays: distinctDays.size};
};
